import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Stack,
  Menu,
  MenuButton,
  MenuList,
  Button,
  Icon,
  Flex,
  Heading,
  IconButton,
  Box,
  Text,
  SystemStyleObject,
  Image,
} from '@chakra-ui/react';
import {
  chakraComponents,
  Select as ReactSelect,
  SingleValueProps,
  GroupBase,
} from 'chakra-react-select';
import {
  mdiCalendar,
  mdiChevronLeft,
  mdiChevronRight,
  mdiClose,
} from '@mdi/js';
import { Global } from '@emotion/react';
import { datePickerStyles } from './datePickerStyles';

type DateView = 'Monthly' | 'Daily' | 'Custom';

interface ViewOption {
  value: DateView;
  label: string;
}

interface DatePickerValue {
  view: DateView;
  currentMonth?: Date; // For Monthly view navigation
  dateRange?: [Date | null, Date | null]; // For Custom view selection
  singleDate?: Date; // For Daily view - single date selection
}

interface DateRangePickerProps {
  value: DatePickerValue;
  onChange: (value: DatePickerValue) => void;
}

const VIEW_OPTIONS: ViewOption[] = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Custom', label: 'Custom' },
];

// Add this DatePickerStyles component for consistent styling
const DatePickerStyles = () => <Global styles={datePickerStyles} />;

// Custom SingleValue component to show "View: {value}"
const CustomViewSingleValue = (
  props: SingleValueProps<ViewOption, boolean, GroupBase<ViewOption>>,
) => {
  const { children, ...rest } = props;
  const selectedValue = children as string;

  return (
    <chakraComponents.SingleValue {...rest}>
      <Text
        color='inherit'
        fontSize='md'
        whiteSpace='nowrap'
        textAlign='left'
        w='full'
        overflow='hidden'
        textOverflow='ellipsis'
      >
        View: {selectedValue}
      </Text>
    </chakraComponents.SingleValue>
  );
};

interface ViewSelectorProps {
  value: DatePickerValue;
  onChange: (value: DatePickerValue) => void;
}

const ViewSelector = ({ value, onChange }: ViewSelectorProps) => {
  const selectedOption = VIEW_OPTIONS.find(
    (option) => option.value === value.view,
  );

  const handleViewChange = (newView: DateView) => {
    // When switching views, preserve what makes sense and reset the rest
    const newValue: DatePickerValue = {
      view: newView,
      currentMonth:
        newView === 'Monthly' ? value.currentMonth || new Date() : undefined,
      dateRange:
        newView === 'Custom' ? value.dateRange || [null, null] : undefined,
      singleDate:
        newView === 'Daily' ? value.singleDate || new Date() : undefined,
    };
    onChange(newValue);
  };

  return (
    <Box
      w={{ base: '100%', lg: 'auto' }}
      minW={{ base: 'auto', lg: '180px' }}
      maxW={{ base: '100%', lg: '220px' }}
    >
      <ReactSelect
        aria-label='Date view type'
        placeholder='View: Select'
        components={{
          ...chakraComponents,
          SingleValue: CustomViewSingleValue,
        }}
        selectedOptionColorScheme='primary'
        value={selectedOption || null}
        chakraStyles={{
          menuList: (provided: SystemStyleObject) => ({
            ...provided,
            p: 0,
          }),
        }}
        options={VIEW_OPTIONS}
        onChange={(data) =>
          handleViewChange((data as ViewOption)?.value || 'Monthly')
        }
      />
    </Box>
  );
};

interface MonthlyViewProps {
  value: DatePickerValue;
  onChange: (value: DatePickerValue) => void;
}

const MonthlyView = ({ value, onChange }: MonthlyViewProps) => {
  const currentDate = value.currentMonth || new Date();
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const displayMonth = currentDate.getMonth();
  const displayYear = currentDate.getFullYear();

  // Check if we're at or past the current month
  const isCurrentOrFutureMonth =
    displayYear > currentYear ||
    (displayYear === currentYear && displayMonth >= currentMonth);

  // Check if we need to show Today button (when navigated away from current month)
  const showTodayButton =
    displayYear !== currentYear ||
    (displayYear === currentYear && displayMonth !== currentMonth);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    onChange({
      ...value,
      currentMonth: newDate,
    });
  };

  const goToToday = () => {
    onChange({
      ...value,
      currentMonth: new Date(),
    });
  };

  return (
    <Flex align='center' gap={4} w='100%'>
      <Flex align='center' gap={2}>
        <IconButton
          variant='ghost'
          size='sm'
          icon={
            <Icon>
              <path d={mdiChevronLeft} />
            </Icon>
          }
          onClick={() => navigateMonth('prev')}
          aria-label='Previous month'
        />
        <Heading size='sm' minW='160px' textAlign='center'>
          {currentDate.toLocaleString('default', {
            month: 'long',
            year: 'numeric',
          })}
        </Heading>
        <IconButton
          variant='ghost'
          size='sm'
          icon={
            <Icon>
              <path d={mdiChevronRight} />
            </Icon>
          }
          onClick={() => navigateMonth('next')}
          aria-label='Next month'
          isDisabled={isCurrentOrFutureMonth}
        />
        {showTodayButton && (
          <Button
            variant='ghost'
            size='md'
            onClick={goToToday}
            leftIcon={
              <Image
                src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-calendar-neutral'
                w={5}
                h={5}
                alt=''
                aria-hidden={true}
              />
            }
            fontSize='16px'
          >
            Today
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

interface DailyViewProps {
  value: DatePickerValue;
  onChange: (value: DatePickerValue) => void;
}

const DailyView = ({ value, onChange }: DailyViewProps) => {
  const currentDate = value.singleDate || new Date();
  const today = new Date();

  // Normalize dates to ignore time for comparison
  const normalizeDate = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const normalizedCurrent = normalizeDate(currentDate);
  const normalizedToday = normalizeDate(today);

  const isToday = normalizedCurrent.getTime() === normalizedToday.getTime();

  const yesterday = new Date(normalizedToday);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = normalizedCurrent.getTime() === yesterday.getTime();

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    onChange({
      ...value,
      singleDate: newDate,
    });
  };

  const goToToday = () => {
    onChange({
      ...value,
      singleDate: new Date(),
    });
  };

  const getDisplayText = () => {
    if (isToday) {
      return 'Today';
    } else if (isYesterday) {
      return 'Yesterday';
    } else {
      // Format like "Monday, 15 Sept"
      const weekday = currentDate.toLocaleDateString('default', {
        weekday: 'long',
      });
      const day = currentDate.toLocaleDateString('default', { day: 'numeric' });
      const month = currentDate.toLocaleDateString('default', {
        month: 'short',
      });
      return `${weekday}, ${day} ${month}`;
    }
  };

  return (
    <Flex align='center' gap={4} w='100%'>
      <Flex align='center' gap={2}>
        <IconButton
          variant='ghost'
          size='sm'
          icon={
            <Icon>
              <path d={mdiChevronLeft} />
            </Icon>
          }
          onClick={() => navigateDay('prev')}
          aria-label='Previous day'
        />
        <Heading size='sm' minW='160px' textAlign='center'>
          {getDisplayText()}
        </Heading>
        <IconButton
          variant='ghost'
          size='sm'
          icon={
            <Icon>
              <path d={mdiChevronRight} />
            </Icon>
          }
          onClick={() => navigateDay('next')}
          aria-label='Next day'
          isDisabled={isToday}
        />
        {!isToday && (
          <Button
            variant='ghost'
            size='md'
            onClick={goToToday}
            leftIcon={
              <Image
                src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-calendar-neutral'
                w={5}
                h={5}
                alt=''
                aria-hidden={true}
              />
            }
            fontSize='16px'
          >
            Today
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

/**
 * Format date to display as "Month Day, Year" (e.g., "Jun 26, 2023")
 */
const formatDate = (date: Date): string => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

/**
 * Format date range for display
 */
const formatDateRange = (
  startDate: Date | null,
  endDate: Date | null,
): string => {
  if (startDate && endDate) {
    return `Date: ${formatDate(startDate)} - ${formatDate(endDate)}`;
  } else if (startDate) {
    return `Date: ${formatDate(startDate)}`;
  }
  return 'Date';
};

/**
 * Custom hook for date range selection with double-click support
 * Now works with controlled component pattern
 */
const useDateRangeSelection = (
  value: DatePickerValue,
  onChange: (value: DatePickerValue) => void,
) => {
  const [lastClickedEndDate, setLastClickedEndDate] = useState<Date | null>(
    null,
  );
  const [lastClickTime, setLastClickTime] = useState<number>(0);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [, end] = dates;

    // Check if this is a double-click on the end date
    const isDoubleClick =
      end &&
      lastClickedEndDate &&
      end.getTime() === lastClickedEndDate.getTime() &&
      Date.now() - lastClickTime < 300;

    if (isDoubleClick) {
      // Double click detected - make end date the new start date
      onChange({
        ...value,
        dateRange: [end, null],
      });
      setLastClickedEndDate(null);
      setLastClickTime(0);
    } else {
      onChange({
        ...value,
        dateRange: dates,
      });
      // If an end date was selected, remember it for potential double-click
      if (end) {
        setLastClickedEndDate(end);
        setLastClickTime(Date.now());
      } else {
        setLastClickedEndDate(null);
      }
    }
  };

  return { handleDateChange };
};

export const DateRangePicker = ({ value, onChange }: DateRangePickerProps) => {
  const { handleDateChange } = useDateRangeSelection(value, onChange);

  const renderCustomView = () => {
    const dateRange = value.dateRange || [null, null];

    return (
      <Flex align='center' gap={3}>
        <Menu>
          <MenuButton
            as={Button}
            variant='filter'
            leftIcon={
              <Icon>
                <path d={mdiCalendar} />
              </Icon>
            }
            rightIcon={
              dateRange[0] || dateRange[1] ? (
                <Icon
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDateChange([null, null]);
                  }}
                >
                  <path d={mdiClose} />
                </Icon>
              ) : undefined
            }
          >
            {formatDateRange(dateRange[0], dateRange[1])}
          </MenuButton>
          <MenuList p='0'>
            <ReactDatePicker
              selected={dateRange[0]}
              onChange={handleDateChange}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              selectsRange
              inline
              showPopperArrow={false}
              placeholderText='No date selected'
              isClearable={true}
              renderCustomHeader={({ decreaseMonth, increaseMonth, date }) => (
                <Flex
                  height='9'
                  paddingY='1'
                  justifyContent='space-between'
                  alignItems='center'
                  alignSelf='stretch'
                  px='2'
                >
                  <IconButton
                    variant='ghost'
                    size='sm'
                    icon={
                      <Icon>
                        <path d={mdiChevronLeft} />
                      </Icon>
                    }
                    onClick={decreaseMonth}
                    aria-label='Previous month'
                  />
                  <Heading size='sm'>
                    {date.toLocaleString('default', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Heading>
                  <IconButton
                    variant='ghost'
                    size='sm'
                    icon={
                      <Icon>
                        <path d={mdiChevronRight} />
                      </Icon>
                    }
                    onClick={increaseMonth}
                    aria-label='Next month'
                  />
                </Flex>
              )}
            />
          </MenuList>
        </Menu>
      </Flex>
    );
  };

  return (
    <Stack gap={4}>
      {/* Include the DatePickerStyles component */}
      <DatePickerStyles />

      {/* View Selector and Navigation/Content Row */}
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        align={{ base: 'stretch', lg: 'center' }}
        gap={3}
        width='100%'
      >
        <ViewSelector value={value} onChange={onChange} />

        {value.view === 'Monthly' && (
          <MonthlyView value={value} onChange={onChange} />
        )}

        {value.view === 'Daily' && (
          <DailyView value={value} onChange={onChange} />
        )}

        {value.view === 'Custom' && renderCustomView()}
      </Flex>
    </Stack>
  );
};

export {
  useDateRangeSelection,
  formatDate,
  formatDateRange,
  ViewSelector,
  MonthlyView,
  DailyView,
};
export type { DateView, ViewOption, DatePickerValue, DateRangePickerProps };
